import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import FeedInventoryTable from './FeedInventoryTable';
import AddFeedItemModal from './AddFeedItemModal';

// --- NEW: Pie Chart Label Rendering ---
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#888" fill="none" />
            <circle cx={ex} cy={ey} r={2} fill="#888" stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 6} y={ey} textAnchor={textAnchor} fill="#333">{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
        </g>
    );
};

const FeedDashboard = ({ 
    feedInventory, 
    onAddFeed, 
    isLoading, 
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    requestSort,
    sortConfig,
    onSelectFeedItem,
    userRole
}) => {
    const [isAddModalOpen, setAddModalOpen] = useState(false);

    const summaryData = useMemo(() => {
        const totalStock = feedInventory.reduce((sum, item) => sum + (item.stock || 0), 0);
        const inventoryValue = feedInventory.reduce((sum, item) => sum + ((item.stock || 0) * (item.unitPrice || 0)), 0);
        return {
            totalTypes: feedInventory.length,
            totalStock,
            inventoryValue,
        };
    }, [feedInventory]);

    // --- Data processing for the Stock Pie Chart ---
    const stockChartData = useMemo(() => {
        return feedInventory.map(item => ({
            name: item.name,
            value: parseFloat(item.stock.toFixed(2))
        })).filter(item => item.value > 0); // Only show items with stock
    }, [feedInventory]);

    // --- NEW: Data processing for the Cost Pie Chart ---
    const costChartData = useMemo(() => {
        return feedInventory.map(item => ({
            name: item.name,
            value: parseFloat(((item.stock || 0) * (item.unitPrice || 0)).toFixed(2))
        })).filter(item => item.value > 0); // Only show items with value
    }, [feedInventory]);

    const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Feed Inventory</h2>
                {/* --- Role-based button visibility --- */}
                {(userRole === 'admin' || userRole === 'manager') && (
                    <button onClick={() => setAddModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">+ Add New Feed Type</button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-gray-500">Total Feed Types</p>
                    <p className="text-2xl font-bold text-gray-800">{summaryData.totalTypes}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-gray-500">Total Stock</p>
                    <p className="text-2xl font-bold text-blue-600">{summaryData.totalStock.toFixed(2)} kg</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-gray-500">Inventory Value</p>
                    <p className="text-2xl font-bold text-green-600">₹{summaryData.inventoryValue.toFixed(2)}</p>
                </div>
            </div>
            
            {/* --- UPDATED: Charts Section with Grid Layout --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Stock Distribution by Quantity</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={stockChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {stockChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} kg`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Stock Distribution by Value</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={costChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {costChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="mb-6 p-4 bg-white rounded-lg shadow flex items-center space-x-4">
                <div className="flex-grow">
                    <input type="text" placeholder="Search by feed name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="block w-full form-input rounded-md shadow-sm" />
                </div>
                <div>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="block w-full form-select rounded-md shadow-sm">
                        <option value="All">All Items</option>
                        <option value="Low Stock">Low Stock</option>
                    </select>
                </div>
            </div>

            <FeedInventoryTable 
                inventory={feedInventory} 
                isLoading={isLoading} 
                onSelectFeedItem={onSelectFeedItem}
                requestSort={requestSort}
                sortConfig={sortConfig}
            />
            
            <AddFeedItemModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)} onSubmit={onAddFeed} />
        </div>
    );
};

export default FeedDashboard;
